import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { CARD_PAY_INTEREST_PAYMENT_CRYPTODRAFT } from '../../mutations';
import { CARD_INTEREST_PAYMENT_CRYPTODRAFT } from '../../queries';
import { cryptodraftScreens } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';
import { RATES_QUERY_KEY } from '../../../../utilities/sharedQueries';

const CryptodraftInterestPayment = () => {
  const { t: translate } = useTranslation();

  const { state, openScreen } = useContext(CryptodraftContext);

  const history = useHistory();

  const { data: cardInterestPayment, refetch: getCardInterestPayment } = useQuery(
    CARD_INTEREST_PAYMENT_CRYPTODRAFT
  );

  const fiatCurrency = state?.fiatCurrency;
  const principalCurrency = state?.principalCurrency;
  const coin = state?.selectedSettlementWallet?.coin;
  const availableBalance = state?.selectedSettlementWallet?.available_balance;

  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const rate = useRate(coin, fiatCurrency);

  const interestPaymentAmountsInFiatCurrency = useMemo(() => {
    if (principalCurrency && rates && cardInterestPayment) {
      return cardInterestPayment.map((item) => {
        const paymentCurrency = item?.currency?.toUpperCase();

        const ratePaymentToFiatCurrency = paymentCurrency
          ? rates[`${paymentCurrency}${fiatCurrency?.toUpperCase()}`]?.ask
          : 1;
        const paymentInFiatCurrency = item?.amount * ratePaymentToFiatCurrency;
        return {
          ...item,
          [`amountIn${fiatCurrency?.toUpperCase()}`]: paymentInFiatCurrency,
        };
      });
    }
    return [];
  }, [principalCurrency, rates, cardInterestPayment, fiatCurrency]);

  const fiatBalance = formatNumberWithComma(
    formatNumber(
      availableBalance * currencies[coin?.toUpperCase()].conversion * rate,
      exchange.FIAT_VALUE
    )
  );

  const totalInterestAmount = useMemo(
    () =>
      interestPaymentAmountsInFiatCurrency.length > 0
        ? interestPaymentAmountsInFiatCurrency
            ?.map((item) => item?.[`amountIn${fiatCurrency?.toUpperCase()}`])
            ?.reduce((prev, next) => prev + next)
        : [],
    [interestPaymentAmountsInFiatCurrency, fiatCurrency]
  );

  const validBalance = useMemo(
    () =>
      availableBalance * currencies[coin?.toUpperCase()].conversion * rate >= totalInterestAmount,
    [totalInterestAmount, rate, availableBalance, coin]
  );

  const { mutate: payInterestPayments, isLoading } = useMutation(
    CARD_PAY_INTEREST_PAYMENT_CRYPTODRAFT,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
          history.push('/dashboard/card/details');
        } else {
          showAlert({
            message: translate('pay_interest_payments_success_msg')
              .replace('%d', formatNumber(totalInterestAmount, exchange.FIAT_VALUE))
              .replace('%s', principalCurrency.toUpperCase()),
            type: 'success',
          });
          getCardInterestPayment();
          history.push('/dashboard/card/details');
        }
      },
    }
  );

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() => history.push('/dashboard/card/details')}
      title={translate('card_interest_payment_title')}>
      <div className="mt-4 pt-1">
        <p className="text-f6 text-dark p-0">{translate('card_interest_payment_note')}</p>
      </div>
      <div className="mt-2">
        <b className="text-f4 text-dark p-0">{`${formatNumber(
          totalInterestAmount,
          exchange.FIAT_VALUE
        )} ${principalCurrency.toUpperCase()}`}</b>
      </div>
      <div
        className="rounded-extra bg-lighter my-3"
        style={{ maxHeight: '15rem', overflowY: 'auto' }}>
        <div className="d-flex flex-column p-0 mb-2">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="text-dark text-f6">{translate('card_interest_payment_due')}</p>
              <p className="text-f6">{cardInterestPayment?.length}</p>
            </div>
          </div>
          {interestPaymentAmountsInFiatCurrency?.map((item) => (
            <React.Fragment key={item.id}>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="text-dark text-f6">
                    {moment(item.period_start_date).format('MMMM YYYY')}
                  </p>
                  <p className="text-f6">{`${formatNumber(
                    item?.[`amountIn${fiatCurrency?.toUpperCase()}`] ?? 0,
                    exchange.FIAT_VALUE
                  )} ${principalCurrency.toUpperCase()}`}</p>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-f6 text-shadow p-0">
          {translate('card_interest_payment_choose_source')}
        </p>
      </div>
      {!validBalance && (
        <div className="alert alert-info mt-2">
          <i className="icon icon-attention-other" />
          <span className="content">
            {translate('not_enough_balance').replace('%d', coin?.toUpperCase())}
          </span>
        </div>
      )}
      <div
        className={`select-group bg-lighter rounded-extra p-3 d-flex flex-row ${
          !validBalance ? 'mt-0' : 'mt-2'
        } `}>
        <div
          role="button"
          tabIndex="0"
          className="d-flex flex-row align-items-center px-2"
          onClick={() =>
            openScreen(cryptodraftScreens.WALLETS, {
              prevScreen: cryptodraftScreens.INTEREST_PAYMENTS,
            })
          }
          onKeyDown={() =>
            openScreen(cryptodraftScreens.WALLETS, {
              prevScreen: cryptodraftScreens.INTEREST_PAYMENTS,
            })
          }>
          <CurrencyIcon currencyCode={coin} className="me-2" />
          <p className="m-0 me-2 text-f5">{coin?.toUpperCase()}</p>
          <i className="icon icon-arrow-down-navigation" />
        </div>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {formatNumber(
              availableBalance * currencies[coin?.toUpperCase()].conversion,
              currencies[coin?.toUpperCase()].decimal
            )}{' '}
            {coin?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {fiatBalance} {fiatCurrency.toUpperCase()}
          </p>
        </span>
      </div>
      <div className="btn-container">
        <button
          disabled={!validBalance}
          type="button"
          className="btn py-3 btn-primary w-100"
          loading={isLoading ? 'true' : 'false'}
          onClick={() => {
            payInterestPayments({ coin });
          }}>
          {translate('pay_now_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default CryptodraftInterestPayment;
